import React, { useEffect } from 'react'
import ConfigLayout from '../Layouts/ConfigLayout'
import isAuthenticated from '../../routes/PrivateRoute'
import { Col, Container, Row } from 'react-bootstrap'
import { Link, useParams, Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { 
  addRuleAction, 
  createAction, 
  createUser, 
  deleteAction, 
  deleteRuleAction, 
  deleteUser, 
  getActions, 
  getHealthSystem, 
  getRules, 
  getUsers, 
  resendSetup, 
  resetHealthSystemOrderState, 
  resetHealthSystemRuleState, 
  resetHealthSystemUserState, 
  sortActions, 
  updateAction, 
  updateActionOrder, 
  updateRule, 
  updateUser,
  getReport
} from '../../redux/actions/healthSystem'
import Landing from './Landing'
import { capitalizeFirstLetter, isSuperAdmin } from '../../utils/helpers'
import Users from './Users'
import Rules from './Rules'
import Actions from './Actions'
import { forgotPasswordUser, resetUserState } from '../../redux/actions/user'
import Reports from './Reports'

const HealthSystem = () => {
  const { tenant, screen } = useParams()

  const dispatch = useDispatch()

  const user = useSelector((state) => state.user.user)

  const { healthSystem, getHealthSystemState } = useSelector((state) => ({ 
    healthSystem: state.healthSystem.healthSystem,
    getHealthSystemState: state.healthSystem.getHealthSystem,
  }))

  const { users, getUsersState, createUserState, deleteUserState, resendSetupState, updateUserState, forgotPasswordState } = useSelector((state) => ({
    users: state.healthSystem.users,
    getUsersState: state.healthSystem.getUsers,
    createUserState: state.healthSystem.createUser,
    deleteUserState: state.healthSystem.deleteUser,
    resendSetupState: state.healthSystem.resendSetup,
    updateUserState: state.healthSystem.updateUser,
    forgotPasswordState: state.user.forgotPassword
  }))

  const { getRulesState, rules, updateRuleState, addRuleActionState, deleteRuleActionState, actions } = useSelector((state) => ({
    getRulesState: state.healthSystem.getRules,
    rules: state.healthSystem.rules,
    updateRuleState: state.healthSystem.updateRule,
    addRuleActionState: state.healthSystem.addRuleAction,
    deleteRuleActionState: state.healthSystem.deleteRuleAction,
    actions: state.healthSystem.actions
  }))

  const { addActionState, updateActionState, deleteActionState, activeActionSort, isDesc } = useSelector((state) => ({
    addActionState: state.healthSystem.addAction,
    updateActionState: state.healthSystem.updateAction,
    deleteActionState: state.healthSystem.deleteAction,
    activeActionSort: state.healthSystem.activeActionSort,
    isDesc: state.healthSystem.isDesc
  }))

  const { reportData, getReportState } = useSelector((state) => ({
    reportData: state.healthSystem.reportData,
    getReportState: state.healthSystem.getReport
  }))

  useEffect(() => {
    dispatch(getHealthSystem(tenant))
  }, [])

  useEffect(() => {
    if (getHealthSystemState.success) {
      if (isSuperAdmin(user)) {
        dispatch(getUsers(healthSystem.healthSystemId))
      }
      dispatch(getRules(healthSystem.healthSystemId))
      dispatch(getActions(healthSystem.healthSystemId))
    }
  }, [getHealthSystemState.success])

  const handleUpdateRule = (opportunityId, rule) => {
    dispatch(updateRule(opportunityId, rule))
  }

  const handleAddRuleAction = (opportunityId, ruleId, actionId) => {
    dispatch(addRuleAction(opportunityId, ruleId, actionId))
  }

  const handleDeleteRuleAction = (opportunityId, ruleId, actionId) => {
    dispatch(deleteRuleAction(opportunityId, ruleId, actionId))
  }

  const handleCreateUser = (user) => {
    dispatch(createUser({
      ...user,
      healthSystemId: healthSystem.healthSystemId
    }))
  }

  const handleUpdateUser = (userId, status) => {
    dispatch(updateUser(userId, status))
  }

  const handleDeleteUser = (userId) => {
    dispatch(deleteUser(userId))
  }

  const handleResendSetup = (userId) => {
    dispatch(resendSetup(userId))
  }

  const handleForgotPassword = (email) => {
    dispatch(forgotPasswordUser(email))
  }

  const handleCreateAction = (action) => {
    dispatch(createAction(action))
  }

  const handleUpdateActionOrder = (actionId, name, value) => {
    dispatch(updateActionOrder(actionId, name, value))
  }

  const handleUpdateAction = (action) => {
    dispatch(updateAction(action))
  }

  const handleDeleteAction = (actionId) => {
    dispatch(deleteAction(actionId))
  }

  const handleSortActions = (sortBy) => {
    dispatch(sortActions(sortBy))
  }

  const handleGetReport = (values) => {
    dispatch(getReport(values))
  }

  const handleResetState = () => {
    switch (screen) {
    case 'rules':
      dispatch(resetHealthSystemRuleState())
      break
    case 'orders':
      dispatch(resetHealthSystemOrderState())
      break
    case 'users':
      dispatch(resetHealthSystemUserState())
      dispatch(resetUserState())
      break
    default:
      break
    }
  }

  const renderScreen = () => {
    switch (screen) {
    case 'rules': {
      if (isSuperAdmin(user)) {
        return (
          <Rules
            rules={rules}
            getRulesState={getRulesState}
            addRuleAction={handleAddRuleAction}
            addRuleActionState={addRuleActionState}
            deleteRuleAction={handleDeleteRuleAction}
            deleteRuleActionState={deleteRuleActionState}
            updateRule={handleUpdateRule}
            updateRuleState={updateRuleState}
            actions={actions}
            reset={handleResetState}
          />
        )
      } else {
        if (tenant === user.healthSystem.tenant) {
          return (
            <Rules
              rules={rules}
              getRulesState={getRulesState}
              addRuleAction={handleAddRuleAction}
              addRuleActionState={addRuleActionState}
              deleteRuleAction={handleDeleteRuleAction}
              deleteRuleActionState={deleteRuleActionState}
              updateRule={handleUpdateRule}
              updateRuleState={updateRuleState}
              actions={actions}
              reset={handleResetState}
            />
          )
        }

        return  <Redirect to={`/health-system/${user.healthSystem.tenant}/rules`} />
      }
    }
    case 'users':
      if (isSuperAdmin(user)) {
        return (
          <Users 
            users={users} 
            getUsersState={getUsersState} 
            createUser={handleCreateUser} 
            createUserState={createUserState}
            updateUser={handleUpdateUser}
            updateUserState={updateUserState}
            deleteUser={handleDeleteUser}
            deleteUserState={deleteUserState}
            resendSetup={handleResendSetup}
            resendSetupState={resendSetupState}
            forgotPassword={handleForgotPassword}
            forgotPasswordState={forgotPasswordState}
            reset={handleResetState}
          />
        )
      }
      return <Redirect to={`/health-system/${healthSystem.tenant}/rules`} />
    case 'actions':
      return (
        <Actions
          user={user}
          healthSystemId={healthSystem.healthSystemId}
          actions={actions}
          createAction={handleCreateAction}
          addActionState={addActionState}
          updateActionOrder={handleUpdateActionOrder}
          updateAction={handleUpdateAction}
          updateActionState={updateActionState}
          deleteAction={handleDeleteAction}
          deleteActionState={deleteActionState}
          activeActionSort={activeActionSort}
          isDesc={isDesc}
          sortActions={handleSortActions}
          reset={handleResetState}
        />
      )
    case 'reports':
      if (isSuperAdmin(user)) {
        return (
          <Reports 
            healthSystemId={healthSystem.healthSystemId}
            reportData={reportData}
            getReport={handleGetReport}
            getReportState={getReportState}
          />
        )
      }
      return <Redirect to={`/health-system/${healthSystem.tenant}/rules`} />
    default:
      return <Landing healthSystem={healthSystem} user={user} />
    }
  }
  
  return (
    <ConfigLayout showUser>
      {
        !getHealthSystemState.loading ? (
          <>
            <Row className='secondary-menu'>
              <Col xs={12}>
                <Container fluid>
                  <Row>
                    <Col xs={screen === 'rules' ? 6 : 12} className='my-1'>
                      <Row>
                        <Col xs={10}>
                          {
                            isSuperAdmin(user) 
                              ? (
                                <>
                                  <Link to='/configurator' className='btn btn-outline-dark btn-sm'>Home</Link>  &gt;
                                  <Link to={`/health-system/${healthSystem.tenant}`} className='btn btn-outline-dark btn-sm ml-2'>{healthSystem.name}</Link>
                                  {
                                    screen && (
                                      <span className='ml-2'>
                                        &gt; {capitalizeFirstLetter(screen)}
                                      </span>
                                    )
                                  }
                                </>
                              ) 
                              : screen !== undefined
                                ? (
                                  <>
                                    <Link to={`/health-system/${healthSystem.tenant}`} className='btn btn-outline-dark btn-sm ml-2'>Home</Link>
                                    {
                                      screen && (
                                        <span className='ml-2'>
                                          &gt; {capitalizeFirstLetter(screen)}
                                        </span>
                                      )
                                    }
                                  </>
                                ) : healthSystem.name
                          }
                        </Col>
                        {
                          screen === 'rules' && (
                            <Col xs={2} className='text-right pr-5'>
                              Status
                            </Col>
                          )
                        }
                      </Row>
                    </Col>
                    {
                      screen === 'rules' && (
                        <Col xs={6} className='my-1'>
                          <Row>
                            <Col xs={6}>
                        Actions
                            </Col>
                            <Col xs={6} className='text-right pr-5'>
                        ID
                            </Col>
                          </Row>
                        </Col>
                      )
                    }
                  </Row>
                </Container>
              </Col>
            </Row>
            <Row className='mt-2'>
              {
                renderScreen()
              }
            </Row>
          </>
        ) : null
      }
      
    </ConfigLayout>
  )
}

export default isAuthenticated(HealthSystem)
